import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { 
  Bot, Send, Zap, Shield, DollarSign, TrendingDown,
  CheckCircle, Clock, AlertTriangle, Sparkles, Settings,
  Play, Pause, Target, Brain, MessageSquare, ArrowRight,
  Server, Database, Cloud, Cpu, HardDrive, RefreshCw,
  ChevronRight, Mic, MicOff, Volume2, Trash2, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AICommandCenter = () => {
  const { shouldShowData } = useDemoData();
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: "Hey! I'm CARTIE, your AI cloud cost manager. I monitor & take action across all 6 LIVE clouds — AWS, Azure, GCP, Snowflake, Databricks & DigitalOcean. Tell me what you want to achieve, and I'll make it happen.",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [autoPilot, setAutoPilot] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const [goals, setGoals] = useState(shouldShowData ? [
    { id: 1, goal: 'Keep monthly spend under $50,000', status: 'active', progress: 78 },
    { id: 2, goal: 'Reduce compute costs by 30%', status: 'active', progress: 45 },
    { id: 3, goal: 'Zero idle resources', status: 'completed', progress: 100 }
  ] : []);

  const [recentActions, setRecentActions] = useState(shouldShowData ? [
    { id: 1, action: 'Rightsized 3 AWS EC2 instances', savings: 450, time: '2 mins ago', status: 'completed' },
    { id: 2, action: 'Set Snowflake auto-suspend to 60s', savings: 380, time: '15 mins ago', status: 'completed' },
    { id: 3, action: 'Moved Databricks job to Job Compute (2.5x cheaper)', savings: 580, time: '32 mins ago', status: 'completed' },
    { id: 4, action: 'Deleted 5 unattached AWS EBS volumes', savings: 125, time: '1 hour ago', status: 'completed' },
    { id: 5, action: 'Enabled DigitalOcean Spaces lifecycle policy', savings: 95, time: '2 hours ago', status: 'completed' },
    { id: 6, action: 'Converting GCP RDS to Committed Use...', savings: 890, time: 'In progress', status: 'pending' }
  ] : []);

  const [stats, setStats] = useState(shouldShowData ? {
    totalSaved: 47230,
    actionsToday: 12,
    efficiency: 94,
    autopilotSavings: 23400
  } : {
    totalSaved: 0,
    actionsToday: 0,
    efficiency: 0,
    autopilotSavings: 0
  });

  const quickCommands = [
    { label: 'Optimize everything', icon: Zap, command: 'Optimize all my cloud resources and apply savings automatically' },
    { label: 'Find waste', icon: AlertTriangle, command: 'Find and remove all wasted resources across my infrastructure' },
    { label: 'Reduce by 20%', icon: TrendingDown, command: 'Create a plan to reduce my cloud costs by 20% this month' },
    { label: 'Set budget', icon: Target, command: 'Help me set up a monthly budget of $40,000 with alerts' }
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize session on mount
  useEffect(() => {
    const storedSession = localStorage.getItem('cartie_session_id');
    if (storedSession) {
      setSessionId(storedSession);
      loadChatHistory(storedSession);
    }
  }, []);

  const loadChatHistory = async (sid) => {
    try {
      const response = await fetch(`${API_URL}/api/ai-command/history/${sid}`);
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          const formattedMessages = data.messages.map((msg, idx) => ({
            id: idx,
            type: msg.role === 'user' ? 'user' : 'ai',
            content: msg.content,
            timestamp: new Date(msg.timestamp)
          }));
          setMessages(formattedMessages);
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = input;
    setInput('');
    setIsProcessing(true);

    try {
      const response = await fetch(`${API_URL}/api/ai-command/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: currentInput,
          session_id: sessionId
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();
      
      // Store session ID for future messages
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem('cartie_session_id', data.session_id);
      }

      // Add AI response
      setMessages(prev => [...prev, {
        id: Date.now() + Math.random(),
        type: 'ai',
        content: data.content,
        action: data.is_action,
        timestamp: new Date(data.timestamp)
      }]);

      // Update stats if action was taken
      if (data.is_action) {
        setStats(prev => ({
          ...prev,
          actionsToday: prev.actionsToday + 1
        }));
      }

    } catch (error) {
      console.error('AI chat error:', error);
      toast.error('Failed to get AI response. Please try again.');
      
      // Add error message
      setMessages(prev => [...prev, {
        id: Date.now() + Math.random(),
        type: 'ai',
        content: "I'm having trouble connecting right now. Please try again in a moment.",
        timestamp: new Date()
      }]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuickCommand = (command) => {
    setInput(command);
  };

  const toggleAutoPilot = () => {
    setAutoPilot(!autoPilot);
    if (!autoPilot) {
      toast.success('Auto-Pilot ENABLED - I\'ll optimize automatically', { duration: 4000 });
      setMessages(prev => [...prev, {
        id: Date.now(),
        type: 'ai',
        content: "**Auto-Pilot Activated!**\n\nI'm now actively managing your cloud costs. I'll:\n\n- Apply safe optimizations automatically\n- Alert you before major changes\n- Keep you under budget\n- Report daily summaries\n\nRelax - I've got this!",
        timestamp: new Date()
      }]);
    } else {
      toast.info('Auto-Pilot disabled');
    }
  };

  const startNewChat = async () => {
    // Clear current session
    if (sessionId) {
      try {
        await fetch(`${API_URL}/api/ai-command/history/${sessionId}`, {
          method: 'DELETE'
        });
      } catch (error) {
        console.error('Failed to clear history:', error);
      }
    }
    
    // Reset state
    setSessionId(null);
    localStorage.removeItem('cartie_session_id');
    setMessages([{
      id: 1,
      type: 'ai',
      content: "Hey! I'm CARTIE, your AI cloud cost manager. I monitor & take action across all 6 LIVE clouds — AWS, Azure, GCP, Snowflake, Databricks & DigitalOcean. Tell me what you want to achieve, and I'll make it happen.",
      timestamp: new Date()
    }]);
    toast.success('Started new conversation');
  };

  // Format message content with markdown-like styling
  const formatContent = (content) => {
    // Split by lines and process
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      // Headers
      if (line.startsWith('# ')) {
        return <h2 key={idx} className="text-lg font-bold mt-2 mb-1">{line.slice(2)}</h2>;
      }
      if (line.startsWith('## ')) {
        return <h3 key={idx} className="text-base font-semibold mt-2 mb-1">{line.slice(3)}</h3>;
      }
      // Bold text
      let processed = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      // Bullet points
      if (line.startsWith('- ') || line.startsWith('• ')) {
        return <p key={idx} className="ml-4 my-0.5" dangerouslySetInnerHTML={{ __html: '• ' + processed.slice(2) }} />;
      }
      // Empty lines
      if (line.trim() === '') {
        return <br key={idx} />;
      }
      return <p key={idx} className="my-0.5" dangerouslySetInnerHTML={{ __html: processed }} />;
    });
  };

  return (
    <div className="h-[calc(100vh-120px)] flex gap-4" data-testid="ai-command-center">
      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
              <Brain className="w-6 h-6 text-slate-950" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                AI Command Center
                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                  Claude Sonnet 4.5
                </Badge>
              </h1>
              <p className="text-sm text-slate-400">Tell me what you want. I'll make it happen.</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* New Chat Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={startNewChat}
              className="text-xs"
              data-testid="new-chat-btn"
            >
              <Plus className="w-3 h-3 mr-1" />
              New Chat
            </Button>
            
            {/* Auto-Pilot Toggle */}
            <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-800 rounded-full px-4 py-2">
              <span className="text-sm font-medium text-slate-400 dark:text-slate-300">Auto-Pilot</span>
              <Switch 
                checked={autoPilot} 
                onCheckedChange={toggleAutoPilot}
                className={autoPilot ? 'bg-emerald-500' : ''}
                data-testid="autopilot-toggle"
              />
              {autoPilot && (
                <Badge className="bg-emerald-500 text-white animate-pulse">
                  <Zap className="w-3 h-3 mr-1" />
                  Active
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Messages */}
        <Card className="flex-1 overflow-hidden flex flex-col">
          <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                data-testid={`message-${msg.type}`}
              >
                <div className={`max-w-[80%] ${msg.type === 'user' ? 'order-2' : ''}`}>
                  {msg.type === 'ai' && (
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 rounded-lg bg-emerald-500 flex items-center justify-center">
                        <Bot className="w-3 h-3 text-slate-950" />
                      </div>
                      <span className="text-xs text-slate-400">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
                      {msg.action && (
                        <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">
                          <Zap className="w-2 h-2 mr-1" />
                          Action Taken
                        </Badge>
                      )}
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-3 ${
                      msg.type === 'user'
                        ? 'bg-emerald-500 text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white'
                    }`}
                  >
                    <div className="text-sm">
                      {msg.type === 'ai' ? formatContent(msg.content) : msg.content}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {isProcessing && (
              <div className="flex justify-start">
                <div className="bg-slate-100 dark:bg-slate-800 rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-slate-400">CARTIE is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </CardContent>

          {/* Quick Commands */}
          <div className="px-4 py-2 border-t border-slate-200 dark:border-slate-700">
            <div className="flex gap-2 overflow-x-auto pb-2">
              {quickCommands.map((cmd, idx) => (
                <Button
                  key={idx}
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickCommand(cmd.command)}
                  className="flex-shrink-0 text-xs"
                  data-testid={`quick-cmd-${idx}`}
                >
                  <cmd.icon className="w-3 h-3 mr-1" />
                  {cmd.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-4 border-t border-slate-200 dark:border-slate-700">
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsListening(!isListening)}
                className={isListening ? 'bg-red-100 text-red-600 border-red-300' : ''}
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Tell CARTIE what you want... (e.g., 'Reduce costs by 30%')"
                className="flex-1"
                disabled={isProcessing}
                data-testid="chat-input"
              />
              <Button 
                onClick={handleSend} 
                disabled={isProcessing || !input.trim()}
                className="bg-emerald-500 hover:bg-emerald-600 text-slate-950"
                data-testid="send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {/* Side Panel */}
      <div className="w-80 space-y-4 hidden lg:block">
        {/* Stats */}
        <Card className="bg-emerald-500 text-slate-950 border-0">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-emerald-950/70 mb-3">AI Performance</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-2xl font-bold">${(stats.totalSaved / 1000).toFixed(1)}K</p>
                <p className="text-xs text-emerald-950/70">Saved This Month</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.actionsToday}</p>
                <p className="text-xs text-emerald-950/70">Actions Today</p>
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.efficiency}%</p>
                <p className="text-xs text-emerald-950/70">Efficiency</p>
              </div>
              <div>
                <p className="text-2xl font-bold">${(stats.autopilotSavings / 1000).toFixed(1)}K</p>
                <p className="text-xs text-emerald-950/70">Auto-Pilot Saved</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Active Goals */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Target className="w-4 h-4 text-emerald-500" />
              Active Goals
            </h3>
            {goals.length > 0 ? (
              <div className="space-y-3">
                {goals.map((goal) => (
                  <div key={goal.id} className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-400 dark:text-slate-400">{goal.goal}</span>
                      <span className={goal.status === 'completed' ? 'text-emerald-500' : 'text-emerald-500'}>
                        {goal.progress}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${goal.status === 'completed' ? 'bg-emerald-500' : 'bg-emerald-500'}`}
                        style={{ width: `${goal.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-4">Connect cloud providers to set optimization goals</p>
            )}
            <Button variant="outline" size="sm" className="w-full mt-3 text-xs">
              <Target className="w-3 h-3 mr-1" />
              Add New Goal
            </Button>
          </CardContent>
        </Card>

        {/* Recent Actions */}
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              Recent Actions
            </h3>
            {recentActions.length > 0 ? (
              <div className="space-y-2">
                {recentActions.map((action) => (
                  <div key={action.id} className="flex items-start gap-2 text-xs">
                    {action.status === 'completed' ? (
                      <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                    ) : (
                      <RefreshCw className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5 animate-spin" />
                    )}
                    <div className="flex-1">
                      <p className="text-slate-700 dark:text-slate-300">{action.action}</p>
                      <div className="flex justify-between text-slate-400">
                        <span>{action.time}</span>
                        <span className="text-emerald-500 font-medium">+${action.savings}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400 text-center py-4">No actions yet. Ask CARTIE to optimize your costs!</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AICommandCenter;
